import * as React from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { z } from 'zod';
import { Button } from './Button';
import { FormField, type FormSchema, type ZodForm } from './Form';
import { Input } from './Input';
import { Inline, Stack } from './Layout';
import { List, ListItem } from './List';
import { Text } from './Text';

/**
 * Who is signing up, as a schema fragment for the wizard to spread into its
 * own. The wizard validates only this slice on its first step — see
 * `OnboardingPage` — so nothing on a later step blocks Continue here.
 */
export const contactInfoShape = {
  fullName: z
    .string()
    .trim()
    .min(1, 'Please enter your full name')
    .min(2, 'Please enter your full name'),
  email: z
    .string()
    .trim()
    .min(1, 'Please enter your email address')
    .email('Please enter a valid email address'),
  /** US format with the dash, as the field formats it while typing. */
  phoneNumber: z
    .string()
    .trim()
    .min(1, 'Please enter your phone number')
    .regex(
      /^\(\d{3}\) \d{3}-\d{4}$/,
      'Please enter a valid 10-digit US phone number (e.g., (555) 123-4567)'
    ),
  password: z.string().min(1, 'Please choose a password'),
  confirmPassword: z.string().min(1, 'Please confirm your password'),
};

export const contactInfoSchema = z.object(contactInfoShape);

export type ContactInfoValues = z.infer<typeof contactInfoSchema>;

export interface ContactInfoStepProps<S extends FormSchema> {
  /** The wizard's form; its schema spreads `contactInfoShape`. */
  form: ZodForm<S>;
}

const PASSWORD_RULES: { label: string; test: (password: string) => boolean }[] =
  [
    { label: 'At least 8 characters', test: (p) => p.length >= 8 },
    {
      label: 'Special characters (!@#$%^&*)',
      test: (p) => /[!@#$%^&*]/.test(p),
    },
    { label: 'Lowercase letters (a-z)', test: (p) => /[a-z]/.test(p) },
    { label: 'Uppercase letters (A-Z)', test: (p) => /[A-Z]/.test(p) },
    { label: 'Numbers (0-9)', test: (p) => /[0-9]/.test(p) },
  ];

const isValidPassword = (password: string) =>
  PASSWORD_RULES.every((rule) => rule.test(password));

/**
 * Formats a US number as the user types — `(555) 123-4567` — and leaves
 * anything that already carries a `+` or a `.` alone, so an international
 * number survives.
 */
const formatPhoneNumber = (value: string) => {
  const cleaned = value.replace(/[^\d+() .-]/g, '');

  if (!cleaned.includes('+') && !cleaned.includes('.')) {
    const digits = cleaned.replace(/\D/g, '');
    if (digits.length >= 7) {
      return digits.replace(/(\d{3})(\d{3})(\d{0,4})/, '($1) $2-$3');
    } else if (digits.length >= 4) {
      return digits.replace(/(\d{3})(\d{0,3})/, '($1) $2');
    } else if (digits.length >= 1) {
      return digits.replace(/(\d{0,3})/, '($1');
    }
  }

  return cleaned;
};

/**
 * Press-and-hold reveal for a password field: the text shows while the mouse
 * is down on the eye and hides again on release or when the pointer leaves.
 */
function RevealButton({
  shown,
  onToggle,
}: {
  shown: boolean;
  onToggle: () => void;
}) {
  return (
    <Button
      size="inline"
      variant="subtle"
      type="button"
      className="pointer-events-auto"
      aria-label={shown ? 'Hide password' : 'Show password'}
      onMouseDown={(e) => {
        e.preventDefault();
        onToggle();
      }}
      onMouseUp={(e) => {
        e.preventDefault();
        onToggle();
      }}
      onMouseLeave={() => {
        if (shown) onToggle();
      }}
    >
      {shown ? <EyeOff /> : <Eye />}
    </Button>
  );
}

/**
 * The first onboarding step: who is signing up. Name, US phone, email and a
 * password with its confirmation. Validates as it goes — each field shows its
 * error once it has something in it — and the password field lists the rules
 * while it is focused or still short of them. The page keeps the values and
 * decides when the step is complete.
 *
 *   <ContactInfoStep values={formData} onChange={updateFormData} />
 */
export function ContactInfoStep<S extends FormSchema>({
  form,
}: ContactInfoStepProps<S>) {
  // See SaleRecordFields: the five names are on the caller's schema, which
  // TypeScript cannot see through the generic.
  const f = form as unknown as ZodForm<typeof contactInfoSchema>;
  const [isPasswordFocused, setIsPasswordFocused] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);

  const password = f.watch('password');
  const passwordInvalid = password !== '' && !isValidPassword(password);

  return (
    <Stack gap={6}>
      <FormField form={f} name="fullName" label="Full name" density="relaxed">
        {(field) => (
          <Input {...field} placeholder="Enter your full name" size="xl" />
        )}
      </FormField>

      <FormField
        form={f}
        name="phoneNumber"
        label="Phone number (with country code)"
        density="relaxed"
      >
        {(field) => (
          <Inline gap={2} align="center">
            <Text variant="body-lg" as="span">
              🇺🇸
            </Text>
            <Text variant="body-lg" tone="muted" as="span">
              +1
            </Text>
            <Input
              {...field}
              type="tel"
              onChange={(e) =>
                field.onChange(formatPhoneNumber(e.target.value))
              }
              placeholder="(555) 123-4567"
              size="xl"
              maxLength={20}
            />
          </Inline>
        )}
      </FormField>

      <FormField form={f} name="email" label="Email address" density="relaxed">
        {(field) => (
          <Input
            {...field}
            type="email"
            placeholder="Enter your email address"
            size="xl"
          />
        )}
      </FormField>

      <FormField form={f} name="password" label="Password" density="relaxed">
        {(field) => (
          <Stack gap={2}>
            <Input
              {...field}
              type={showPassword ? 'text' : 'password'}
              onFocus={() => setIsPasswordFocused(true)}
              onBlur={() => {
                setIsPasswordFocused(false);
                field.onBlur();
              }}
              placeholder="Enter your password"
              size="xl"
              aria-invalid={passwordInvalid || undefined}
              trailingIcon={
                <RevealButton
                  shown={showPassword}
                  onToggle={() => setShowPassword((s) => !s)}
                />
              }
            />
            {password && (isPasswordFocused || passwordInvalid) && (
              <Stack gap={1}>
                <Text variant="body-sm" tone="muted">
                  Password must contain:
                </Text>
                <List gap={1}>
                  {PASSWORD_RULES.map((rule) => (
                    <ListItem
                      key={rule.label}
                      tone={rule.test(password) ? 'success' : 'muted'}
                    >
                      {rule.label}
                    </ListItem>
                  ))}
                </List>
              </Stack>
            )}
          </Stack>
        )}
      </FormField>

      <FormField
        form={f}
        name="confirmPassword"
        label="Confirm password"
        density="relaxed"
      >
        {(field) => (
          <Input
            {...field}
            type={showConfirmPassword ? 'text' : 'password'}
            placeholder="Confirm your password"
            size="xl"
            trailingIcon={
              <RevealButton
                shown={showConfirmPassword}
                onToggle={() => setShowConfirmPassword((s) => !s)}
              />
            }
          />
        )}
      </FormField>
    </Stack>
  );
}

/** The password rules, for a wizard that wants to state them in its schema. */
export { isValidPassword };
